import React, { useState, useEffect } from 'react';
import { getTokenStatus, updateToken } from '../api';

type Status = { valid: boolean; demo: boolean; reason?: string } | null;

export default function TokenWarning() {
  const [status, setStatus] = useState<Status>(null);
  const [open, setOpen] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    getTokenStatus()
      .then(res => setStatus({ valid: !!res.data.valid, demo: !!res.data.demo, reason: res.data.reason }))
      .catch(() => {});
  }, []);

  if (!status || status.valid) return null;

  const isDemo = status.demo;
  const isBlocked = status.reason === 'blocked';

  const handleSave = async () => {
    const t = tokenInput.trim();
    if (!t) return;
    setSaving(true);
    setSaveError('');
    try {
      await updateToken(t);
      window.location.reload();
    } catch (e: any) {
      setSaveError(e.response?.data?.error || 'Fehler beim Speichern');
      setSaving(false);
    }
  };

  return (
    <div style={isDemo ? s.demoBanner : s.errorBanner}>
      <div style={s.topRow}>
        {isDemo ? <InfoIcon color="#92400e" /> : <WarnIcon />}
        <span style={{ flex: 1 }}>
          {isDemo ? (
            <><strong>Demo-Modus</strong> – keine echten Daten</>
          ) : isBlocked ? (
            <><strong>Meta hat den API-Zugriff gesperrt</strong> („API access blocked"). Neues Token nötig – Daten können nicht geladen werden.</>
          ) : (
            <><strong>Meta Token abgelaufen.</strong> Daten können nicht geladen werden.</>
          )}
        </span>
        {!isDemo && (
          <a
            href="https://developers.facebook.com/tools/explorer/"
            target="_blank"
            rel="noreferrer"
            style={s.genLink}
          >
            Token generieren →
          </a>
        )}
        <button
          onClick={() => setOpen(o => !o)}
          style={isDemo ? s.demoBtn : s.errorBtn}
        >
          {open ? 'Schließen' : 'Token eingeben'}
        </button>
      </div>

      {open && (
        <div style={s.inputRow}>
          <input
            type="text"
            placeholder="EAAf42ZAs9tN0BR…"
            value={tokenInput}
            onChange={e => setTokenInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            style={isDemo ? s.inputDemo : s.inputError}
            autoFocus
          />
          <button
            onClick={handleSave}
            disabled={saving || !tokenInput.trim()}
            style={isDemo ? s.demoBtn : s.errorBtn}
          >
            {saving ? 'Speichern…' : 'Speichern & aktivieren'}
          </button>
          {saveError && <span style={s.errText}>{saveError}</span>}
        </div>
      )}
    </div>
  );
}

function InfoIcon({ color }: { color: string }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

const s: Record<string, React.CSSProperties> = {
  demoBanner: {
    background: '#fffbeb',
    border: '1px solid rgba(245,158,11,0.4)',
    borderRadius: 12,
    padding: '11px 18px',
    marginBottom: 24,
    color: '#92400e',
    fontSize: 13,
  },
  errorBanner: {
    background: '#fff2f2',
    border: '1px solid rgba(255,59,48,0.25)',
    borderRadius: 12,
    padding: '11px 18px',
    marginBottom: 24,
    color: '#cc2a22',
    fontSize: 13,
  },
  topRow: { display: 'flex', alignItems: 'center', gap: 10 },
  genLink: {
    color: '#cc2a22', fontWeight: 600, textDecoration: 'none',
    whiteSpace: 'nowrap' as const, fontSize: 12,
  },
  demoBtn: {
    background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 8,
    padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap' as const, flexShrink: 0,
  },
  errorBtn: {
    background: '#cc2a22', color: '#fff', border: 'none', borderRadius: 8,
    padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    whiteSpace: 'nowrap' as const, flexShrink: 0,
  },
  inputRow: {
    display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
    flexWrap: 'wrap' as const,
  },
  inputDemo: {
    flex: 1, minWidth: 200, background: '#fff',
    border: '1.5px solid rgba(245,158,11,0.5)',
    borderRadius: 8, padding: '8px 12px', fontSize: 12,
    color: '#1d1d1f', outline: 'none', fontFamily: 'monospace',
  },
  inputError: {
    flex: 1, minWidth: 200, background: '#fff',
    border: '1.5px solid rgba(255,59,48,0.4)',
    borderRadius: 8, padding: '8px 12px', fontSize: 12,
    color: '#1d1d1f', outline: 'none', fontFamily: 'monospace',
  },
  errText: { color: '#cc2a22', fontSize: 11 },
};
